import { getSystemTimeZone } from "./timezone";
import type { UnitsSettings } from "./units";
const ISO_DATE_LENGTH = 10;
const MIDNIGHT_HOUR_START = "00";
const MIDNIGHT_HOUR_END = "24";
const MINUTE_ON_THE_HOUR = "00";
const SECONDS_TIMESTAMP_LIMIT = 1e12;
const MS_PER_SECOND = 1000;
const DECIMAL_RADIX = 10;
const MONEY_FRACTION_DIGITS = 2;
const ENGLISH_LANGUAGE = "en";
const TIME_PART_WIDTH = 2;
const HHMM_HOUR_DIGITS = 2;
const HMM_DIGIT_COUNT = 3;
const HHMM_DIGIT_COUNT = 4;
const MAX_HOUR = 23;
const MAX_MINUTE = 59;
const MINUTES_PER_HOUR = 60;
const MAX_MINUTE_OF_DAY = 24 * MINUTES_PER_HOUR - 1;
const DECIMAL_PROBE_VALUE = 1.1;
const GROUPED_PROBE_VALUE = 12345.6;
const PERCENT_SCALE = 100;

export const DEFAULT_UNITS_LOCALE = "de-DE";
/** Letzter Notnagel, wenn der Browser keine Zeitzone kennt — nicht die Standard-Anzeigezone. */
export const DEFAULT_UNITS_TIME_ZONE = "Europe/Berlin";
export const DEFAULT_UNITS_CURRENCY = "EUR";
export const TIME_REGEX = /^([01]?\d|2[0-3]):[0-5]\d$/;
const TIME_WITH_OPTIONAL_SECONDS_REGEX = /^([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/;

export function localeOf(units?: UnitsSettings | null): string {
  const locale = (units?.locale || "").trim();
  return locale || DEFAULT_UNITS_LOCALE;
}

export function numberLocaleOf(units?: UnitsSettings | null): string {
  const locale = (units?.numberLocale || units?.locale || "").trim();
  return locale || DEFAULT_UNITS_LOCALE;
}

/**
 * Anzeige-Zeitzone: Basis ist die Zeitzone des Browsers, die eingestellte Zone gewinnt.
 * Kein Produkt zeigt eine feste Zone an — was nicht eingestellt ist, folgt dem Gerät.
 */
export function timeZoneOf(units?: UnitsSettings | null): string {
  const timeZone = (units?.timeZone || "").trim();
  return timeZone || getSystemTimeZone(DEFAULT_UNITS_TIME_ZONE);
}

export function currencyOf(units?: UnitsSettings | null): string {
  const currency = (units?.currency || units?.currencyCode || "").trim();
  return currency || DEFAULT_UNITS_CURRENCY;
}

function toDateOnlyString(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString().slice(0, ISO_DATE_LENGTH);
  if (typeof value === "number" && !Number.isNaN(value)) return new Date(value).toISOString().slice(0, ISO_DATE_LENGTH);
  return String(value);
}

export function parseDateOnly(dateOnly: string | unknown): Date | null {
  const value = toDateOnlyString(dateOnly).trim();
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function formatDateOnly(dateOnly: string | unknown, units?: UnitsSettings | null, opts?: Intl.DateTimeFormatOptions): string {
  const date = parseDateOnly(dateOnly);
  if (!date) return "";
  const defaultOpts: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric" };
  return date.toLocaleDateString(localeOf(units), { ...defaultOpts, ...opts });
}

export function formatDateRange(
  startDate: string | unknown,
  endDate: string | unknown,
  units?: UnitsSettings | null,
  fallback = "-",
  separator = " - ",
): string {
  const start = formatDateOnly(startDate, units);
  if (!start) return fallback;
  const end = formatDateOnly(endDate, units);
  return end ? `${start}${separator}${end}` : start;
}

export function formatDateList(
  dates: readonly unknown[],
  units?: UnitsSettings | null,
  fallback = "",
  separator = ", ",
): string {
  const formattedDates = dates.map(date => formatDateOnly(date, units)).filter(Boolean);
  return formattedDates.length > 0 ? formattedDates.join(separator) : fallback;
}

export function formatDayMonth(dateOnly: string | unknown, units?: UnitsSettings | null): string {
  const date = parseDateOnly(dateOnly);
  if (!date) return "";
  return date.toLocaleDateString(localeOf(units), { day: "2-digit", month: "2-digit" });
}

export const DATE_TIME_2DIGIT_OPTS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

export function formatDateTimeIso(iso: string, units?: UnitsSettings | null, opts?: Intl.DateTimeFormatOptions): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = { ...(opts || {}) };
  const timeZone = timeZoneOf(units);
  if (timeZone) options.timeZone = timeZone;
  return date.toLocaleString(localeOf(units), options);
}

function toDisplayDate(value: string | number | Date | null | undefined): Date | null {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Datumsanzeige aller Produkte: Tag und Monat zweistellig, Jahr vierstellig, in der
 * Anzeige-Zeitzone.
 *
 * Reine Datumswerte (`YYYY-MM-DD`) tragen keinen Zeitpunkt und werden deshalb nicht
 * umgerechnet — sonst würde ein Geburtstag westlich von Berlin auf den Vortag rutschen.
 */
export function formatDateDisplay(
  value: string | number | Date | null | undefined,
  units?: UnitsSettings | null,
  fallback = "",
): string {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return formatDateOnly(value, units) || fallback;
  }
  const date = toDisplayDate(value);
  if (!date) return fallback;
  return new Intl.DateTimeFormat(localeOf(units), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: timeZoneOf(units),
  }).format(date);
}

export type FormatDateTimeDisplayOptions = {
  /** Deutsche Anzeigekonvention einzelner Produkte: „12.08.2025, 14:30 Uhr". */
  clockSuffix?: boolean;
};

/** Datum und Uhrzeit in der Anzeige-Zeitzone, Sekunden bleiben weg. */
export function formatDateTimeDisplay(
  value: string | number | Date | null | undefined,
  units?: UnitsSettings | null,
  opts?: FormatDateTimeDisplayOptions,
  fallback = "",
): string {
  const date = toDisplayDate(value);
  if (!date) return fallback;
  const formatted = new Intl.DateTimeFormat(localeOf(units), {
    ...DATE_TIME_2DIGIT_OPTS,
    timeZone: timeZoneOf(units),
  }).format(date);
  if (!opts?.clockSuffix) return formatted;
  return localeOf(units).toLowerCase().startsWith("de") && /\d{1,2}:\d{2}$/.test(formatted)
    ? `${formatted} Uhr`
    : formatted;
}

/**
 * Liegt der Zeitpunkt in der Anzeige-Zeitzone auf Mitternacht?
 *
 * Wird genutzt, um Datumswerte ohne gesetzte Uhrzeit ohne „, 00:00" anzuzeigen. Die Prüfung muss
 * in der Anzeige-Zeitzone erfolgen — im gespeicherten UTC-String steht je nach Zone etwas anderes
 * (z. B. „T22:00:00Z" für lokale Mitternacht bei UTC+2).
 */
export function isMidnightInDisplayZone(iso: string, units?: UnitsSettings | null): boolean {
  if (!iso) return false;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return false;
  const options: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hour12: false };
  const timeZone = timeZoneOf(units);
  if (timeZone) options.timeZone = timeZone;
  const parts = new Intl.DateTimeFormat("en-GB", options).formatToParts(date);
  const hour = parts.find((p) => p.type === "hour")?.value;
  const minute = parts.find((p) => p.type === "minute")?.value;
  return (hour === MIDNIGHT_HOUR_START || hour === MIDNIGHT_HOUR_END) && minute === MINUTE_ON_THE_HOUR;
}

export function formatDateTimeFlexible(
  value: string | number | Date | null | undefined,
  units?: UnitsSettings | null,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (value == null || value === "") return "";
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    return formatDateTimeIso(value.toISOString(), units, opts);
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const milliseconds = value < SECONDS_TIMESTAMP_LIMIT ? value * MS_PER_SECOND : value;
    return formatDateTimeIso(new Date(milliseconds).toISOString(), units, opts);
  }

  const stringValue = String(value).trim();
  if (/^\d{10}$/.test(stringValue)) return formatDateTimeIso(new Date(parseInt(stringValue, DECIMAL_RADIX) * MS_PER_SECOND).toISOString(), units, opts);
  if (/^\d{13}$/.test(stringValue)) return formatDateTimeIso(new Date(parseInt(stringValue, DECIMAL_RADIX)).toISOString(), units, opts);
  return formatDateTimeIso(stringValue, units, opts);
}

export type FormatDateTimeNoSecondsOptions = {
  omitTimeWhenMidnight?: boolean;
  showMidnightTime?: boolean;
};

function extractIsoDatePart(value: string): string {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  return match?.[1] ?? value;
}

function isMidnightDateTime(value: string): boolean {
  return /[T ]00:00(?::00(?:\.0+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/i.test(value.trim());
}

export function formatDateTimeNoSeconds(
  iso: string,
  units?: UnitsSettings | null,
  opts?: FormatDateTimeNoSecondsOptions,
): string {
  if (opts?.omitTimeWhenMidnight && !opts.showMidnightTime && (/^\d{4}-\d{2}-\d{2}$/.test(iso.trim()) || isMidnightDateTime(iso))) {
    return formatDateOnly(extractIsoDatePart(iso), units);
  }

  const dateFormat = (units?.dateFormat || "").trim() || "DD.MM.YYYY";
  const day2 = dateFormat.includes("DD") || dateFormat.includes("dd");
  const month2 = dateFormat.includes("MM");
  return formatDateTimeIso(iso, units, {
    day: day2 ? "2-digit" : "numeric",
    month: month2 ? "2-digit" : "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatMoney(amount: number, units?: UnitsSettings | null): string {
  const locale = localeOf(units);
  const currency = currencyOf(units);
  const value = Number(amount || 0);
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(value);
  } catch {
    return `${value.toFixed(MONEY_FRACTION_DIGITS)} ${currency}`;
  }
}

/**
 * Geldbetrag in einer ausdrücklich genannten Währung — etwa Anbieterkosten in USD, während der
 * Mandant in EUR rechnet. <see cref="formatMoney"/> nähme hier die Mandantenwährung und beschriftete
 * den Betrag damit falsch.
 *
 * Ohne gültigen Währungscode wird bewusst nur die Zahl formatiert: Ein ergänztes Währungssymbol
 * wäre eine Angabe, die die Daten nicht hergeben.
 */
export function formatMoneyIn(
  amount: number,
  currency: string | null | undefined,
  units?: UnitsSettings | null,
  opts?: Intl.NumberFormatOptions,
): string {
  const locale = localeOf(units);
  const value = Number(amount || 0);
  const code = (currency ?? "").trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(code)) return formatNumber(value, units, opts);

  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency: code, ...opts }).format(value);
  } catch {
    return `${formatNumber(value, units, opts)} ${code}`;
  }
}

export function formatNumber(amount: number, units?: UnitsSettings | null, opts?: Intl.NumberFormatOptions): string {
  const locale = numberLocaleOf(units);
  const value = Number(amount || 0);
  try {
    return new Intl.NumberFormat(locale, opts).format(value);
  } catch {
    return String(value);
  }
}

export function formatWalletBalance(balance: number, walletType: number, units?: UnitsSettings | null): string {
  const type = Number(walletType || 0);
  if (type === 1) {
    return `${formatNumber(balance, units, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Punkte`;
  }
  return formatMoney(balance, units);
}

export function dateInputFormatOf(units?: UnitsSettings | null): string {
  const locale = localeOf(units).toLowerCase();
  if (locale.startsWith("en-us") || locale === ENGLISH_LANGUAGE) return "MM/DD/YYYY";
  if (locale.startsWith("en-")) return "DD/MM/YYYY";
  return "DD.MM.YYYY";
}

export function dateTimeInputFormatOf(units?: UnitsSettings | null): string {
  return `${dateInputFormatOf(units)} HH:mm`;
}

export function normalizeTimeToHHmm(value: unknown): string {
  const text = String(value ?? "").trim();
  if (!text) return "";

  const timeMatch = TIME_WITH_OPTIONAL_SECONDS_REGEX.exec(text);
  if (timeMatch) {
    return `${timeMatch[1].padStart(TIME_PART_WIDTH, "0")}:${timeMatch[2]}`;
  }

  const digits = text.replace(/\D/g, "");
  if (!digits) return "";

  let hours: number;
  let minutes: number;
  if (digits.length <= HHMM_HOUR_DIGITS) {
    hours = parseInt(digits, DECIMAL_RADIX);
    minutes = 0;
  } else if (digits.length === HMM_DIGIT_COUNT) {
    hours = parseInt(digits.slice(0, 1), DECIMAL_RADIX);
    minutes = parseInt(digits.slice(1, HMM_DIGIT_COUNT), DECIMAL_RADIX);
  } else {
    hours = parseInt(digits.slice(0, HHMM_HOUR_DIGITS), DECIMAL_RADIX);
    minutes = parseInt(digits.slice(HHMM_HOUR_DIGITS, HHMM_DIGIT_COUNT), DECIMAL_RADIX);
  }

  if (hours < 0 || hours > MAX_HOUR || minutes < 0 || minutes > MAX_MINUTE) return "";
  return `${String(hours).padStart(TIME_PART_WIDTH, "0")}:${String(minutes).padStart(TIME_PART_WIDTH, "0")}`;
}

export function isValidTime(value: string): boolean {
  return typeof value === "string" && TIME_REGEX.test(value.trim());
}

export function hhmmToMinutes(hhmm: string): number | null {
  const normalized = normalizeTimeToHHmm(hhmm);
  if (!normalized || !isValidTime(normalized)) return null;
  const [hours, minutes] = normalized.split(":").map((part) => parseInt(part, DECIMAL_RADIX));
  return hours * MINUTES_PER_HOUR + minutes;
}

export function minutesToHHmm(minutes: number): string {
  const safeMinutes = Math.max(0, Math.min(MAX_MINUTE_OF_DAY, Math.floor(minutes)));
  const hours = Math.floor(safeMinutes / MINUTES_PER_HOUR);
  const remainingMinutes = safeMinutes % MINUTES_PER_HOUR;
  return `${String(hours).padStart(TIME_PART_WIDTH, "0")}:${String(remainingMinutes).padStart(TIME_PART_WIDTH, "0")}`;
}

export function dateInputFormatFromUiLang(lang: string | undefined | null): string {
  return (lang || "").toLowerCase() === ENGLISH_LANGUAGE ? "MM/DD/YYYY" : "DD.MM.YYYY";
}

export function formatNumberFromUiLang(value: number, lang: string | undefined | null, opts?: Intl.NumberFormatOptions): string {
  const locale = (lang || "").toLowerCase() === ENGLISH_LANGUAGE ? "en-US" : "de-DE";
  const numberValue = Number(value || 0);
  try {
    return new Intl.NumberFormat(locale, opts).format(numberValue);
  } catch {
    return String(numberValue);
  }
}

export function decimalSeparatorOf(units?: UnitsSettings | null): string {
  const locale = numberLocaleOf(units);
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(DECIMAL_PROBE_VALUE);
    return parts.find(part => part.type === "decimal")?.value || ",";
  } catch {
    return ",";
  }
}

export function moneyPlaceholderOf(units?: UnitsSettings | null): string {
  return `0${decimalSeparatorOf(units)}00`;
}

export function formatDecimalInput(value: string | number | undefined | null, units?: UnitsSettings | null, opts?: Intl.NumberFormatOptions): string {
  if (value === undefined || value === null || value === "") return "";
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return formatNumber(numeric, units, opts);
}

export function parseDecimalInput(value: string | undefined | null, units?: UnitsSettings | null): number {
  if (!value) return 0;
  const locale = numberLocaleOf(units);
  let groupSeparator = ",";
  let decimalSeparator = ".";

  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(GROUPED_PROBE_VALUE);
    groupSeparator = parts.find(part => part.type === "group")?.value || groupSeparator;
    decimalSeparator = parts.find(part => part.type === "decimal")?.value || decimalSeparator;
  } catch {
    // Keep fallback separators.
  }

  const normalized = value
    .replace(new RegExp(`\\${groupSeparator}`, "g"), "")
    .replace(decimalSeparator, ".")
    .replace(/[^\d.-]/g, "");
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : 0;
}

export function parseOptionalDecimalInput(value: string | undefined | null, units?: UnitsSettings | null): number | "" {
  const raw = (value ?? "").trim();
  return raw ? parseDecimalInput(raw, units) : "";
}

/**
 * Prozentanzeige aus einem 0..100-Wert (nicht 0..1). Beispiel: 25 => "25 %".
 */
export function formatPercent(value: number | undefined | null, units?: UnitsSettings | null, maximumFractionDigits = 0): string {
  const locale = numberLocaleOf(units);
  try {
    return new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits }).format((value ?? 0) / PERCENT_SCALE);
  } catch {
    return `${value ?? 0}%`;
  }
}

function currencySymbolOf(units?: UnitsSettings | null): string {
  const currency = currencyOf(units);
  try {
    const parts = new Intl.NumberFormat(numberLocaleOf(units), { style: "currency", currency }).formatToParts(0);
    return parts.find(part => part.type === "currency")?.value || currency;
  } catch {
    return currency;
  }
}

/**
 * Formatiert einen Eingabewert als Währungstext für Eingabefelder: gruppierter
 * Betrag mit 2 Nachkommastellen plus Währungssymbol. Symbolposition folgt der Locale
 * (en-* voranstellend, sonst nachstellend). Leerer Eingabewert => leerer String.
 */
export function formatCurrencyInput(value: string | number | undefined | null, units?: UnitsSettings | null): string {
  const formatted = formatDecimalInput(value, units, { minimumFractionDigits: MONEY_FRACTION_DIGITS, maximumFractionDigits: MONEY_FRACTION_DIGITS });
  if (!formatted) return "";
  const symbol = currencySymbolOf(units);
  const prefix = numberLocaleOf(units).toLowerCase().startsWith("en");
  return prefix ? `${symbol}${formatted}` : `${formatted} ${symbol}`;
}

/**
 * Parse-Formate für Datums-Eingabefelder (Day.js-Muster), abgeleitet aus der Locale.
 * Erste Position ist das kanonische Anzeigeformat.
 */
export function getDateInputFormats(units?: UnitsSettings | null): string[] {
  const locale = localeOf(units).toLowerCase();
  if (locale.startsWith("en-us") || locale === ENGLISH_LANGUAGE) {
    return ["MM/DD/YYYY", "M/D/YYYY", "MM/DD/YY", "M/D/YY", "YYYY-MM-DD"];
  }
  if (locale.startsWith("en-")) {
    return ["DD/MM/YYYY", "D/M/YYYY", "DD/MM/YY", "D/M/YY", "YYYY-MM-DD"];
  }
  return ["DD.MM.YYYY", "D.M.YYYY", "DD.MM.YY", "D.M.YY", "YYYY-MM-DD"];
}

/**
 * Parse-Formate für Uhrzeit-Eingabefelder (Day.js-Muster), locale-unabhängig.
 */
export function getTimeInputFormats(): string[] {
  return ["HH:mm", "H:mm", "HH.mm", "H.mm", "HHmm", "Hmm"];
}
